import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { map } from "lodash";
import MainLayout from "@/Component/MainLayout";
import Breadcrumbs from "@/Component/Common/Breadcrumbs";
import CustomerReviews from "@/Component/Common/CustomerReviews";
import FaqSection from "@/Component/Common/FaqSection";
import PageHero from "@/Component/Common/PageHero";
import SeeTheWorld from "@/Component/Common/SeeTheWorld";
import DownloadNH from "@/Component/Common/DownloadNH";
import CountrywiseCard from "@/Component/Module/Deals/CountrywiseCard";
import { scrollToSectionById } from "@/utils/scrollToSection";
import { fetchSeasonalDealsData } from "@/services/api/common";
import {
  DEFAULT_DEALS_FAQS,
  hasServiceContent,
  mapApiFaqs,
  slugifySectionId,
  stripHtml,
} from "@/utils/dealsHelpers";
import styles from "./SeasonalDeals.module.css";

export default function SeasonalDeals({
  slug = "",
  initialSeasonalDealsData = null,
  initialReviewsList = null,
}) {
  const heroScrollRef = useRef(null);
  const stickyRef = useRef(null);
  const [dealsData, setDealsData] = useState(initialSeasonalDealsData);
  const [loading, setLoading] = useState(!initialSeasonalDealsData);
  const [selectedSection, setSelectedSection] = useState("");

  const loadDealsData = useCallback(async (dealSlug) => {
    if (!dealSlug) return;

    setLoading(true);
    try {
      const response = await fetchSeasonalDealsData({ slug: dealSlug });
      setDealsData(response?.data || null);
    } catch (error) {
      console.error("Failed to load seasonal deals data:", error);
      setDealsData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!slug) return;

    if (initialSeasonalDealsData) {
      setDealsData(initialSeasonalDealsData);
      setLoading(false);
      return;
    }

    loadDealsData(slug);
  }, [slug, initialSeasonalDealsData, loadDealsData]);

  const banner = dealsData?.topBanners?.[0];
  const moduleType = dealsData?.module || "Holiday";
  const visibleSections = useMemo(
    () => (dealsData?.services || []).filter(hasServiceContent),
    [dealsData?.services]
  );

  const scrollToSection = (sectionId) => {
    if (typeof window === "undefined") return;

    window.history.pushState(null, "", `#${sectionId}`);
    if (!scrollToSectionById(sectionId)) return;
    setSelectedSection(sectionId);
  };

  useEffect(() => {
    if (!visibleSections.length) {
      setSelectedSection("");
      return;
    }

    const firstId = slugifySectionId(visibleSections[0].btnName);
    setSelectedSection((prev) => {
      const isValid = visibleSections.some(
        (section) => slugifySectionId(section.btnName) === prev
      );
      return isValid ? prev : firstId;
    });
  }, [visibleSections]);

  useEffect(() => {
    if (!visibleSections.length || typeof window === "undefined") return;

    const hash = window.location.hash.replace("#", "");
    if (!hash) return;

    setTimeout(() => {
      scrollToSection(hash);
    }, 150);
  }, [visibleSections]);

  const faqItems = useMemo(() => {
    const apiFaqs = mapApiFaqs(dealsData?.faqs);
    return apiFaqs.length ? apiFaqs : DEFAULT_DEALS_FAQS;
  }, [dealsData?.faqs]);

  const heroAlt =
    banner?.webAltTag ||
    banner?.mobileAltTag ||
    stripHtml(dealsData?.heading?.title) ||
    dealsData?.pageTitle ||
    "Seasonal deals banner";
  const seeTheWorldTitle =
    dealsData?.heading?.title || "See The World With Next Holidays";
  const seeTheWorldText = dealsData?.heading?.description;

  const webImageUrl = banner?.webImageUrl || "/images/hero.png";

  if (loading) {
    return (
      <MainLayout hideHeader>
        <div className={styles.loadingWrap}>
          <Image
            src="/assets/loading.gif"
            alt="Loading deals"
            width={100}
            height={100}
          />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout hideHeader>
      <PageHero
        webImageUrl={webImageUrl}
        alt={heroAlt}
        scrollTargetRef={heroScrollRef}
      />
      <Breadcrumbs />
      {visibleSections.length > 0 ? (
        <div ref={stickyRef} className={`standard-padding ${styles.stickyWrap}`}>
          <div className={styles.regionTabs} role="tablist" aria-label="Regions"  ref={heroScrollRef}>
            {map(visibleSections, (section) => {
              const sectionId = slugifySectionId(section.btnName);
              const isSelected =
                (selectedSection || slugifySectionId(visibleSections[0].btnName)) ===
                sectionId;

              return (
                <button
                  key={sectionId}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  className={`${styles.regionTab} ${isSelected ? styles.regionTabActive : ""
                    }`}
                  onClick={() => scrollToSection(sectionId)}
                >
                  {section.btnName?.trim()}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {map(visibleSections, (section) => {
        const sectionId = slugifySectionId(section.btnName);

        return (
          <div key={sectionId} id={sectionId}>
            <CountrywiseCard
              titleFirst={section.titleFirst}
              titleLast={section.titleLast}
              description={section.description}
              packageList={section.itemsList || []}
              moduleType={moduleType}
            />
          </div>
        );
      })}

      {!visibleSections.length ? (
        <p className={`nodata ${styles.emptyState}`}>No deals available at the moment</p>
      ) : null}

      <CustomerReviews
        moduleType="SeasonalDealsPage"
        slug={slug}
        initialReviewsList={initialReviewsList}
      />

      <FaqSection
        items={faqItems}
        className="standard-padding"
      />
      <DownloadNH />
      <SeeTheWorld title={seeTheWorldTitle} text={seeTheWorldText || undefined} />


    </MainLayout>
  );
}
